import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Badge, Row, Col } from "react-bootstrap";
import {
  FaBalanceScale,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
} from "react-icons/fa";
import { empleadosService } from "../services/api";
import { toast } from "react-toastify";

const AjustarHorasModal = ({ show, onHide, empleadoId }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState([]);
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && empleadoId) {
      loadMonthlyData();
    }
  }, [show, selectedYear, empleadoId]);

  const loadMonthlyData = async () => {
    if (!empleadoId) {
      toast.error("No se ha seleccionado un empleado");
      return;
    }

    setLoading(true);
    try {
      const response = await empleadosService.getBalanceHoras(
        selectedYear,
        empleadoId
      );

      if (response.data.success) {
        setMonthlyData(response.data.monthlyData);
        setQuarterlyData(response.data.quarterlyData || []);
      } else {
        toast.error(
          response.data.message || "Error al cargar los datos de horas"
        );
        setMonthlyData([]);
        setQuarterlyData([]);
      }
    } catch (error) {
      console.error("Error cargando datos mensuales:", error);
      toast.error(
        error.response?.data?.message || "Error al cargar los datos de horas"
      );
      setMonthlyData([]);
      setQuarterlyData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  };

  const getDifferenceColor = (difference) => {
    if (difference > 0) return "danger"; // Rojo (A Pagar)
    if (difference < 0) return "success"; // Verde (A Regularizar)
    return "secondary";
  };

  const getDifferenceText = (difference) => {
    const absDiff = Math.abs(difference);
    const sign = difference > 0 ? "+" : difference < 0 ? "-" : "";
    return `${sign}${formatHours(absDiff)}`;
  };

  const handleYearChange = (direction) => {
    setSelectedYear((prev) => prev + direction);
  };

  const totalWorked = monthlyData.reduce((sum, item) => sum + item.worked, 0);
  const totalPlanned = monthlyData.reduce((sum, item) => sum + item.planned, 0);
  const totalDifference = totalWorked - totalPlanned;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaBalanceScale className="me-2" />
          Ajustar horas - Empleado {empleadoId}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Navegación por año */}
        <Row className="mb-4">
          <Col className="text-center">
            <div className="d-flex align-items-center justify-content-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handleYearChange(-1)}
                className="me-3"
              >
                <FaChevronLeft />
              </Button>

              <h4 className="mb-0 mx-3">{selectedYear}</h4>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => handleYearChange(1)}
                className="ms-3"
              >
                <FaChevronRight />
              </Button>
            </div>
          </Col>
        </Row>

        {/* Resumen anual */}
        <Row className="mb-4">
          <Col md={4}>
            <div className="text-center p-3 bg-light rounded">
              <FaClock className="text-primary mb-2" size={24} />
              <h5>Horas Trabajadas</h5>
              <h4 className="text-primary">{formatHours(totalWorked)}</h4>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-3 bg-light rounded">
              <FaClock className="text-info mb-2" size={24} />
              <h5>Horas Planificadas</h5>
              <h4 className="text-info">{formatHours(totalPlanned)}</h4>
            </div>
          </Col>
          <Col md={4}>
            <div className="text-center p-3 bg-light rounded">
              <FaBalanceScale
                className={`text-${getDifferenceColor(totalDifference)} mb-2`}
                size={24}
              />
              <h5>Diferencia</h5>
              <h4 className={`text-${getDifferenceColor(totalDifference)}`}>
                {getDifferenceText(totalDifference)}
              </h4>
            </div>
          </Col>
        </Row>

        {/* Tabla de cuatrimestres */}
        <div className="table-responsive mb-4">
          <h5 className="mb-3">
            <FaBalanceScale className="me-2" />
            Resumen por Cuatrimestres
          </h5>
          <Table striped bordered hover size="sm">
            <thead className="table-dark">
              <tr>
                <th>Cuatrimestre</th>
                <th className="text-center">Horas Trabajadas</th>
                <th className="text-center">Horas Planificadas</th>
                <th className="text-center">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {!empleadoId ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No se ha seleccionado un empleado
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </td>
                </tr>
              ) : quarterlyData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No hay datos disponibles para este empleado y año
                  </td>
                </tr>
              ) : (
                quarterlyData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{item.quarter}</strong>
                    </td>
                    <td className="text-center">
                      <FaClock className="text-primary me-1" />
                      {formatHours(item.worked)}
                    </td>
                    <td className="text-center">
                      <FaClock className="text-info me-1" />
                      {formatHours(item.planned)}
                    </td>
                    <td className="text-center">
                      <Badge
                        bg={getDifferenceColor(item.difference)}
                        className="px-3 py-2"
                      >
                        {getDifferenceText(item.difference)}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        {/* Tabla mensual */}
        <div className="table-responsive">
          <h5 className="mb-3">
            <FaClock className="me-2" />
            Detalle Mensual
          </h5>
          <Table striped bordered hover>
            <thead className="table-dark">
              <tr>
                <th>Mes</th>
                <th className="text-center">Horas Trabajadas</th>
                <th className="text-center">Horas Planificadas</th>
                <th className="text-center">Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {!empleadoId ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No se ha seleccionado un empleado
                  </td>
                </tr>
              ) : loading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                  </td>
                </tr>
              ) : monthlyData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No hay datos disponibles para este empleado y año
                  </td>
                </tr>
              ) : (
                monthlyData.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <strong>{item.month}</strong>
                    </td>
                    <td className="text-center">
                      <FaClock className="text-primary me-1" />
                      {formatHours(item.worked)}
                    </td>
                    <td className="text-center">
                      <FaClock className="text-info me-1" />
                      {formatHours(item.planned)}
                    </td>
                    <td className="text-center">
                      <Badge
                        bg={getDifferenceColor(item.difference)}
                        className="px-3 py-2"
                      >
                        {getDifferenceText(item.difference)}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AjustarHorasModal;
